import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {Alert, Box, Typography} from '@mui/material';
import CNumberField from 'crqlar-react-common/dist/components/CNumberField';
import {FabricObject} from 'fabric';
import {SeatConfig, SeatSlot, SeatSlotWithPosition, SlotEdge} from '../types';
import {
    angleDegToEdge,
    buildSeatConfigForCapacity,
    calculateSlotPositions,
    generateSlots,
    getCircleSeatLayout,
    getTableCapacity,
    normalizeAngleDeg,
} from '../utils/fabric-factory';
import {FABRIC_DEFAULTS} from '../utils/fabric-defaults';
import {useFloorplanContext} from '../hooks/useFloorplanContext';
import {TABLE_CAPACITY_MAX} from '../utils/constants';
import {getNormalizedTableCanvas} from '../utils/resize-utils';
import TableSettingsSeatCircleConfigurator from './TableSettingsSeatCircleConfigurator';
import TableSettingsSeatRectConfigurator from './TableSettingsSeatRectConfigurator';
import {
    buildFallbackPriorityById,
    clampAngleToCircleArc,
    normalizeCircleSlotPriorities,
} from '../utils/table-configurator-utils';

interface Props {
    element: FabricObject;
    onElementUpdated: () => void;
}

export const TableSettingsSeatPanel: React.FC<Props> = ({
                                                            element,
                                                            onElementUpdated,
                                                        }) => {
    const {t} = useTranslation();
    const {showSeatsAroundTable, updateTableElementLocally} = useFloorplanContext();

    const tableData = element.floorPlanItem!.table!;
    const shape = tableData.shape ?? 'rectangle';
    const tableCanvas = tableData.canvas;
    const seats = tableData.seats;
    const width = tableCanvas?.width ?? FABRIC_DEFAULTS.table.width;
    const height = tableCanvas?.height ?? FABRIC_DEFAULTS.table.height;
    const seatOffset = seats?.offset ?? FABRIC_DEFAULTS.table.seats.offset;
    const edges = seats?.edges ?? FABRIC_DEFAULTS.table.seats.edges;
    const rawSlots = useMemo(() => seats?.slots ?? [], [seats?.slots]);
    const tableName = tableData.resource?.name ?? t('Table');

    const visualSlots: SeatSlotWithPosition[] = useMemo(() => (
        calculateSlotPositions(
            shape,
            width,
            height,
            seatOffset,
            edges,
            rawSlots,
            seats?.circle
        )
    ), [edges, height, rawSlots, seatOffset, seats?.circle, shape, width]);

    const seatCount = visualSlots.length;
    const fallbackPriorityById = useMemo(
        () => buildFallbackPriorityById(visualSlots),
        [visualSlots]
    );
    const getNormalizedCanvas = useCallback((currentTable: typeof tableData, currentElement: FabricObject) => (
        getNormalizedTableCanvas(
            currentTable.canvas,
            currentTable.shape,
            currentElement.scaleX,
            currentElement.scaleY
        )
    ), []);

    // Apply one table update patch while keeping the canvas geometry normalized first.
    const handleUpdateTableConfig = useCallback((updates: {
        canvas?: Record<string, any>;
        seats?: SeatConfig;
    }) => {
        const nextElement = updateTableElementLocally(element, (currentItem, currentElement) => {
            const currentTable = currentItem.table!;
            // Fabric may still carry temporary scale from interactive transforms.
            const scaledCanvas = getNormalizedCanvas(currentTable, currentElement);
            const mergedSeats = updates.seats ?? currentTable.seats;

            return {
                ...currentItem,
                table: {
                    ...currentTable,
                    canvas: {
                        // Keep the normalized base canvas, then layer in the requested seat-panel changes.
                        ...scaledCanvas,
                        ...updates.canvas,
                    } as any,
                    seats: mergedSeats,
                    resource: mergedSeats
                        ? {
                            ...currentTable.resource,
                            // Resource capacity is derived from the live seat config, so keep them in sync here.
                            capacity: getTableCapacity(
                                mergedSeats,
                                currentTable.resource?.capacity ?? 0
                            ),
                        }
                        : currentTable.resource,
                },
            };
        });
        if (!nextElement) return;
        onElementUpdated();
    }, [element, getNormalizedCanvas, onElementUpdated, updateTableElementLocally]);

    // Replace the full seat model from a target capacity when the user edits total seats directly.
    const handleSetAllSeats = useCallback((count: number) => {
        const nextElement = updateTableElementLocally(element, (currentItem, currentElement) => {
            const currentTable = currentItem.table!;
            // Normalize first so the regenerated seats use the stored table size, not transient Fabric scale.
            const scaledCanvas = getNormalizedCanvas(currentTable, currentElement);
            const nextSeats = buildSeatConfigForCapacity(
                currentTable.shape,
                count,
                currentTable.seats
            );

            return {
                ...currentItem,
                table: {
                    ...currentTable,
                    canvas: scaledCanvas as any,
                    seats: nextSeats,
                    resource: {
                        ...currentTable.resource,
                        // Capacity shown elsewhere in the app should immediately reflect the new seat count.
                        capacity: getTableCapacity(nextSeats, count),
                    },
                },
            };
        });
        if (!nextElement) return;
        onElementUpdated();
    }, [element, getNormalizedCanvas, onElementUpdated, updateTableElementLocally]);

    // Regenerate rectangle seats for one edge while preserving the rest of the table seat config.
    const handleUpdateEdgeSeats = useCallback((edge: SlotEdge, count: number) => {
        const nextElement = updateTableElementLocally(element, (currentItem, currentElement) => {
            const currentTable = currentItem.table!;
            const scaledCanvas = getNormalizedCanvas(currentTable, currentElement);
            const currentSeats = currentTable.seats ?? {
                size: FABRIC_DEFAULTS.table.seats.size,
                offset: FABRIC_DEFAULTS.table.seats.offset,
                edges: FABRIC_DEFAULTS.table.seats.edges,
                slots: [],
            };

            const nextEdges = {...currentSeats.edges, [edge]: count};
            const nextSlots = generateSlots(
                currentTable.shape,
                nextEdges,
                currentSeats.slots,
                currentSeats.circle
            );
            // We rebuild slots here because changing an edge count changes slot ids/positions on the rectangle.
            const nextSeats: SeatConfig = {
                ...currentSeats,
                edges: nextEdges,
                slots: nextSlots,
            };

            return {
                ...currentItem,
                table: {
                    ...currentTable,
                    canvas: scaledCanvas as any,
                    seats: nextSeats,
                    resource: {
                        ...currentTable.resource,
                        // Capacity follows the generated seat config rather than the previous stored resource value.
                        capacity: getTableCapacity(nextSeats, 0),
                    },
                },
            };
        });
        if (!nextElement) return;
        onElementUpdated();
    }, [element, getNormalizedCanvas, onElementUpdated, updateTableElementLocally]);

    // Swap two visible seat priorities without rebuilding the rest of the seat layout.
    const handleSwapSeatPriorities = useCallback((fromId: string, toId: string) => {
        if (!tableData.seats) return;

        const byId = new Map<string, SeatSlot>();
        rawSlots.forEach((slot) => byId.set(slot.id, slot));

        const fromPriority = byId.get(fromId)?.priority ?? fallbackPriorityById.get(fromId) ?? 0;
        const toPriority = byId.get(toId)?.priority ?? fallbackPriorityById.get(toId) ?? 0;

        const nextSlots = rawSlots.map((slot) => {
            if (slot.id === fromId) return {...slot, priority: toPriority};
            if (slot.id === toId) return {...slot, priority: fromPriority};
            return slot;
        });

        if (!byId.has(fromId)) {
            nextSlots.push({id: fromId, priority: toPriority, edge: 'top', position: 0} as SeatSlot);
        }
        if (!byId.has(toId)) {
            nextSlots.push({id: toId, priority: fromPriority, edge: 'top', position: 0} as SeatSlot);
        }

        // Reuse the central table updater so capacity/resource synchronization stays in one place.
        handleUpdateTableConfig({seats: {...tableData.seats, slots: nextSlots}});
    }, [fallbackPriorityById, handleUpdateTableConfig, rawSlots, tableData.seats]);

    // Update the circle arc settings and then rebuild the circle seat model from the current capacity.
    const handleUpdateCircleLayout = useCallback((
        circle: Required<NonNullable<SeatConfig['circle']>>
    ) => {
        const nextElement = updateTableElementLocally(element, (currentItem, currentElement) => {
            const currentTable = currentItem.table!;
            const scaledCanvas = getNormalizedCanvas(currentTable, currentElement);
            const currentSeats = currentTable.seats ?? {
                size: FABRIC_DEFAULTS.table.seats.size,
                offset: FABRIC_DEFAULTS.table.seats.offset,
                edges: FABRIC_DEFAULTS.table.seats.edges,
                slots: [],
                circle: {...FABRIC_DEFAULTS.table.seats.circle},
            };
            // Circle presets change seat distribution, so rebuild from current capacity instead of patching angles in place.
            const currentCapacity = getTableCapacity(
                currentSeats,
                currentTable.resource?.capacity ?? 0
            );
            const nextSeats = buildSeatConfigForCapacity('circle', currentCapacity, {
                ...currentSeats,
                circle,
            });

            return {
                ...currentItem,
                table: {
                    ...currentTable,
                    canvas: scaledCanvas as any,
                    seats: nextSeats,
                    resource: {
                        ...currentTable.resource,
                        // Keep the resource layer aligned with the rebuilt circle seat layout.
                        capacity: getTableCapacity(nextSeats, currentCapacity),
                    },
                },
            };
        });
        if (!nextElement) return;
        onElementUpdated();
    }, [element, getNormalizedCanvas, onElementUpdated, updateTableElementLocally]);

    // Move a single circle seat to a new angle and then renormalize booking priority around the arc.
    const handleUpdateCircleSeatAngle = useCallback((slotId: string, angleDeg: number) => {
        const nextElement = updateTableElementLocally(element, (currentItem, currentElement) => {
            const currentTable = currentItem.table!;
            const scaledCanvas = getNormalizedCanvas(currentTable, currentElement);
            const currentSeats = currentTable.seats ?? {
                size: FABRIC_DEFAULTS.table.seats.size,
                offset: FABRIC_DEFAULTS.table.seats.offset,
                edges: FABRIC_DEFAULTS.table.seats.edges,
                slots: [],
                circle: {...FABRIC_DEFAULTS.table.seats.circle},
            };
            const nextCircle = getCircleSeatLayout(currentSeats);
            const normalizedAngle = clampAngleToCircleArc(
                normalizeAngleDeg(angleDeg),
                nextCircle.startAngleDeg,
                nextCircle.sweepAngleDeg
            );
            // Update only the moved slot first, then let the circle priority normalizer reorder the full ring.
            const updatedSlots = currentSeats.slots.map((slot) => (
                slot.id === slotId
                    ? {...slot, angleDeg: normalizedAngle, edge: angleDegToEdge(normalizedAngle)}
                    : slot
            ));
            const nextSeats: SeatConfig = {
                ...currentSeats,
                circle: nextCircle,
                slots: normalizeCircleSlotPriorities(updatedSlots, nextCircle),
            };

            return {
                ...currentItem,
                table: {
                    ...currentTable,
                    canvas: scaledCanvas as any,
                    seats: nextSeats,
                    resource: {
                        ...currentTable.resource,
                        // Circle drag edits can change booking order, so persist the derived capacity/resource together.
                        capacity: getTableCapacity(
                            nextSeats,
                            currentTable.resource?.capacity ?? 0
                        ),
                    },
                },
            };
        });
        if (!nextElement) return;
        onElementUpdated();
    }, [element, getNormalizedCanvas, onElementUpdated, updateTableElementLocally]);

    return (
        <Box>
            {!showSeatsAroundTable && (
                <>
                    <Typography variant="subtitle2" gutterBottom>
                        {t('Capacity')}
                    </Typography>
                    <CNumberField
                        size="small"
                        fullWidth
                        value={seatCount}
                        onChange={(value: number | null) => (
                            value !== null &&
                            value >= 0 &&
                            handleSetAllSeats(value)
                        )}
                        min={0}
                        max={TABLE_CAPACITY_MAX}
                        sx={{mb: 1}}
                    />
                    <Alert severity="info" sx={{mb: 2}}>
                        {t('Capacity means the number of seats on a table, the amount of people that can be serviced at the same time by the staff member or in the room/facility.')}
                    </Alert>
                </>
            )}

            {showSeatsAroundTable && shape === 'circle' && (
                <TableSettingsSeatCircleConfigurator
                    width={width}
                    height={height}
                    seatOffset={seatOffset}
                    tableName={tableName}
                    seats={tableData.seats}
                    visualSlots={visualSlots}
                    onSetAllSeats={handleSetAllSeats}
                    onUpdateCircleLayout={handleUpdateCircleLayout}
                    onUpdateCircleSeatAngle={handleUpdateCircleSeatAngle}
                />
            )}

            {showSeatsAroundTable && shape === 'rectangle' && (
                <TableSettingsSeatRectConfigurator
                    width={width}
                    height={height}
                    seatOffset={seatOffset}
                    tableName={tableName}
                    edges={edges}
                    visualSlots={visualSlots}
                    onUpdateEdgeSeats={handleUpdateEdgeSeats}
                    onSwapSeatPriorities={handleSwapSeatPriorities}
                />
            )}
        </Box>
    );
};

export default TableSettingsSeatPanel;
